'use client';

import React, {FC, PropsWithChildren} from 'react';
import {isMobile} from 'react-device-detect';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import {usePathname, useSearchParams} from 'next/navigation';

import {Button} from '@zoefin/design-system';

import getIsMobileCompressed from '../../utils/getIsMobileCompressed';
import Logos from '../Logos';

import styles from './styles.module.scss';

export interface LayoutProps {
  progress: false | number;
  // TODO: look for a better (generic) way to type this: 'intro' | 'animation'
  currentStep: string;
  continueButton: {
    isVisible: boolean;
    isValid: boolean;
  };
  ui?: {
    hideFooter?: boolean;
    hideBackButton?: boolean;
    hidePrivacyWidget?: boolean;
    fullScreenStepWrapper?: boolean;
    inlineBackMobile?: boolean;
  };
  prev: () => void;
  next: () => void;
}

const Layout: FC<PropsWithChildren<LayoutProps>> = ({
  progress,
  currentStep,
  continueButton,
  ui,
  children,
  prev,
  next,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryParams = Object.fromEntries(searchParams ?? new URLSearchParams());

  const isNerdwallet = pathname.includes('/forms/nerdwallet');

  const stringPercentage = `${progress}%`;

  const containerClassName = cn(styles.layout, {
    [styles.layout__modWhiteBackground]: ui?.fullScreenStepWrapper === true,
    [styles.layout__modTopAndNoBackground]: ui?.fullScreenStepWrapper === true,
  });
  const stepClassName = cn(styles.layout__step, {
    [styles.layout__step__modAlignTop]: ui?.fullScreenStepWrapper === true,
  });
  const wrapperClassName = cn(styles.stepWrapper, {
    [styles.stepWrapper__modAnimationStep]: currentStep === 'animation',
    [styles.stepWrapper__modVerticalStretch]: currentStep === 'about',
    [styles.stepWrapper__modNoSpacing]: ui?.fullScreenStepWrapper === true,
  });
  const wrapperStepClassName = cn(styles.stepWrapper__step, {
    [styles.stepWrapper__step__modFitContentMobile]:
      ui?.fullScreenStepWrapper === true,
  });

  const isAboutStep = currentStep === 'about';

  const currentYear = new Date().getFullYear();
  const continueBtnLabel = isAboutStep ? 'See Advisor Matches' : 'Continue';

  const iframeHost = queryParams.embed_host || undefined;

  // To reduce scrolling for embedded iframe on third party websites
  const isMobileCompressed = getIsMobileCompressed(iframeHost);

  return (
    <div className={containerClassName}>
      {ui?.hidePrivacyWidget !== true && (
        <div className={styles.layout__privacyWidget}>
          <Image
            src="/static/svg/icon_lock.svg"
            alt="Lock icon"
            width={17}
            height={20}
          />
          <p>
            <b>Your information is secure.</b>
            We use it to curate our advisor recommendations.
          </p>
        </div>
      )}
      <div className={stepClassName}>
        {currentStep === 'intro' ? (
          children
        ) : (
          <div className={wrapperClassName}>
            {progress !== false && (
              <div className={styles.stepWrapper__progress}>
                <div
                  className={styles.stepWrapper__progress_fill}
                  style={{width: stringPercentage}}>
                  {stringPercentage}
                </div>
              </div>
            )}

            <div className={wrapperStepClassName}>{children}</div>

            {currentStep !== 'animation' && (
              <div
                className={cn(styles.stepWrapper__controls, {
                  [styles.stepWrapper__controls__modSingleButton]:
                    continueButton.isVisible,
                  [styles.stepWrapper__controls__modColumnMobile]:
                    !!ui?.inlineBackMobile,
                })}>
                {continueButton.isVisible && (
                  <div className={styles.stepWrapper__controls_continue}>
                    <Button
                      disabled={!continueButton.isValid}
                      onClick={isMobile ? undefined : next}
                      onTouchEnd={isMobile ? next : undefined}
                      withIcon={{
                        iconSrc: 'fa-regular fa-arrow-right',
                        isFontAwesome: true,
                      }}
                      fitParent
                      as="button">
                      {continueBtnLabel}
                    </Button>
                  </div>
                )}

                {isAboutStep && (
                  <p className={styles.stepWrapper__controls_disclosures}>
                    By sharing your e-mail address above and clicking the button
                    below, you consent to the electronic delivery of Zoe
                    Financial’s Form&nbsp;
                    <a
                      href="https://findanadvisor.com/legal/form-adv-part-2a"
                      target="_blank"
                      rel="noreferrer">
                      ADV 2A
                    </a>
                    , Form&nbsp;
                    <a
                      href="https://findanadvisor.com/legal/zoe-financial-inc-customer-relationship-summary"
                      target="_blank"
                      rel="noreferrer">
                      CRS
                    </a>
                    , and&nbsp;
                    <a
                      href="https://findanadvisor.com/solicitation-disclosures"
                      target="_blank"
                      rel="noreferrer">
                      Solicitor’s Disclosure
                    </a>
                    .
                  </p>
                )}

                {ui?.hideBackButton !== true && (
                  <>
                    <div
                      className={cn(styles.stepWrapper__controls_mobileBack, {
                        [styles.stepWrapper__controls_mobileBack__modHidden]:
                          !!ui?.inlineBackMobile,
                      })}>
                      {!isAboutStep && (
                        <Button
                          as="button"
                          config={{
                            color: 'blue',
                            type: 'secondary',
                          }}
                          withIcon={{
                            iconSrc: '/static/svg/icon_arrow_left_gray.svg',
                            iconPosition: 'left',
                          }}
                          onClick={() => prev()}
                        />
                      )}
                    </div>
                    <div
                      className={cn(styles.stepWrapper__controls_desktopBack, {
                        [styles.stepWrapper__controls_desktopBack__modInlineMobile]:
                          !!ui?.inlineBackMobile,
                      })}>
                      <Button
                        as="button"
                        config={{
                          color: 'blue',
                          type: isAboutStep ? 'secondary' : 'tertiary',
                        }}
                        onClick={() => prev()}
                        withIcon={
                          isAboutStep
                            ? undefined
                            : {
                                iconSrc: '/static/svg/icon_arrow_left_gray.svg',
                                iconPosition: 'left',
                              }
                        }>
                        {isAboutStep ? 'Go back' : 'Back'}
                      </Button>
                    </div>
                    <div
                      className={cn(
                        styles.stepWrapper__controls_desktopBackInline,
                        {
                          [styles.stepWrapper__controls_desktopBackInline__modInlineMobile]:
                            !!ui?.inlineBackMobile,
                        },
                      )}>
                      <Button
                        className={
                          styles.stepWrapper__controls_desktopBackInline
                        }
                        as="button"
                        config={{
                          color: 'blue',
                          type: 'secondary',
                        }}
                        onClick={() => prev()}>
                        Back
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isAboutStep && (
              <Button
                as="button"
                className={styles.stepWrapper__controls_aboutBack}
                config={{
                  color: 'blue',
                  type: 'secondary',
                }}
                onClick={() => prev()}>
                Go Back
              </Button>
            )}

            {!isMobileCompressed && (
              <div className={styles.stepWrapper__footerMobile}>
                <div
                  className={cn(
                    styles.stepWrapper__footerMobile_secureInformation,
                    {
                      [styles.stepWrapper__footerMobile_secureInformation_modAbout]:
                        currentStep === 'about',
                    },
                  )}>
                  <Image
                    width={20}
                    height={20}
                    src="/static/svg/blue-lock-outline.svg"
                    alt="Secure information icon"
                  />

                  <p> Your information is secure. </p>
                </div>

                <Logos />
              </div>
            )}
          </div>
        )}
      </div>
      {ui?.hideFooter !== true && (
        <footer className={styles.layout__footer}>
          <section className={styles.layout__footer_copyright}>
            <Logos />
            <span>
              © {currentYear} Zoe Financial, Inc. | All rights reserved
            </span>
            {isNerdwallet && (
              <span>
                © 2017-{currentYear} and TM, NerdWallet, Inc. All Rights
                Reserved.
              </span>
            )}
          </section>
          <section className={styles.layout__footer_nav}>
            <Link
              href="https://findanadvisor.com/global-disclosure"
              target="_blank"
              rel="noreferrer">
              Disclosures
            </Link>
            <Link
              href="https://findanadvisor.com/privacy-policy"
              target="blank">
              Privacy Policy
            </Link>
            <Link href="https://findanadvisor.com/contact" target="blank">
              Contact Us
            </Link>
            <Link href="https://findanadvisor.com/terms-of-use" target="blank">
              Terms of Use
            </Link>
          </section>
        </footer>
      )}
    </div>
  );
};

export default Layout;
